import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type {
  ApplicationChangeReasonOptionDto,
  ApplicationEditableFieldDto,
} from '../../api/admin';
import {
  MIN_REASON_DETAIL_LENGTH,
  formatFieldValue,
  initialEditValue,
  renderValue,
  validateEditForm,
} from '../../lib/applicationFieldEditing';

/**
 * Enum options for the employment fields whose values are a fixed set.
 *
 * Mirrors the orchestrator registry's `z.enum`. A free-text box here would let a
 * reviewer type a value the verification service cannot match, and the failure
 * would surface much later as an unexplained mismatch.
 */
const ENUM_OPTIONS: Record<string, ReadonlyArray<{ value: string; label: string }>> = {
  'employment.employmentType': [
    { value: 'FULL_TIME', label: 'Full time' },
    { value: 'PART_TIME', label: 'Part time' },
    { value: 'SEASONAL', label: 'Seasonal' },
    { value: 'SELF_EMPLOYED', label: 'Self employed' },
    { value: 'UNEMPLOYED', label: 'Unemployed' },
    { value: 'RETIRED', label: 'Retired' },
    { value: 'STUDENT', label: 'Student' },
    { value: 'DISABILITY_OR_LONG_TERM_LEAVE', label: 'Disability or long-term leave' },
  ],
  'employment.workType': [
    { value: 'ONSITE', label: 'On site' },
    { value: 'HYBRID', label: 'Hybrid' },
    { value: 'REMOTE', label: 'Remote' },
  ],
};

/** Per-field guidance where getting the format wrong has a real consequence. */
const FIELD_HINTS: Record<string, string> = {
  'agreement.dob': 'ISO format, YYYY-MM-DD. Ambiguous dates are the commonest new error.',
  'personal.dateOfBirth': 'ISO format, YYYY-MM-DD.',
  'employment.startDate': 'ISO format, YYYY-MM-DD.',
  'loan.requestedAmount': 'The principal the customer asked for. The capacity snapshot is unchanged.',
};

export interface ApplicationFieldEditDialogProps {
  open: boolean;
  field: ApplicationEditableFieldDto | null;
  reasonCodes: readonly ApplicationChangeReasonOptionDto[];
  saving: boolean;
  /** Server-side failure, shown inline — a reviewer must not lose their typing to a toast. */
  errorMessage: string | null;
  /** True when the failure means they have to reload before retrying. */
  mustReload: boolean;
  onClose: () => void;
  onSubmit: (input: { value: string; reasonCode: string; reasonDetail: string }) => void;
}

export function ApplicationFieldEditDialog({
  open,
  field,
  reasonCodes,
  saving,
  errorMessage,
  mustReload,
  onClose,
  onSubmit,
}: ApplicationFieldEditDialogProps) {
  const [value, setValue] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [reasonDetail, setReasonDetail] = useState('');

  // Reset on open so a previous field's typing never leaks into the next edit.
  useEffect(() => {
    if (open && field) {
      setValue(initialEditValue(field));
      setReasonCode('');
      setReasonDetail('');
    }
  }, [open, field]);

  const validity = useMemo(
    () => (field ? validateEditForm(field, { value, reasonCode, reasonDetail }) : null),
    [field, value, reasonCode, reasonDetail],
  );

  if (!field) return null;

  const enumOptions = ENUM_OPTIONS[field.key];
  const hint = FIELD_HINTS[field.key];
  const isOverlay = field.writeMode === 'OVERLAY';

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography component="span" variant="h6" sx={{ fontWeight: 700 }}>
          Edit {field.label.toLowerCase()}
        </Typography>
        {/* No table or column here — that is storage detail, recorded on the
            change_log row rather than shown to a reviewer. What does belong is the
            one thing an overlay changes about the outcome: the owning service keeps
            its own value, and this correction sits beside it. */}
        {isOverlay ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            Stored as a correction against this application — {field.targetService} keeps
            its own value.
          </Typography>
        ) : null}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2.25} sx={{ pt: 0.5 }}>
          {errorMessage ? (
            <Alert severity={mustReload ? 'warning' : 'error'} variant="outlined">
              {errorMessage}
              {mustReload ? (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Your typing is kept here — reload the application in another tab to see their
                  change, then reapply yours if it still stands.
                </Typography>
              ) : null}
            </Alert>
          ) : null}

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              CURRENT VALUE
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.25 }}>
              {formatFieldValue(field)}
            </Typography>
            {field.overridden && field.sourceResolved !== false ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {field.targetService} still holds{' '}
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {renderValue(field.sourceValue, field.valueType)}
                </Box>
                . That evidence is not changed by this edit.
              </Typography>
            ) : null}
            {field.sourceResolved === false ? (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.25 }}>
                {field.sourceUnresolvedReason ??
                  'The owning service could not be reached, so the current value is unknown.'}{' '}
                Your correction will still be recorded.
              </Typography>
            ) : null}
          </Box>

          <Divider />

          {enumOptions ? (
            <TextField
              select
              label="New value"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              disabled={saving}
              fullWidth
              size="small"
            >
              {enumOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField
              label="New value"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              disabled={saving}
              fullWidth
              size="small"
              multiline={field.valueType === 'string' && /address/i.test(field.key)}
              minRows={field.valueType === 'string' && /address/i.test(field.key) ? 2 : undefined}
              helperText={hint}
              autoFocus
            />
          )}

          <TextField
            select
            label="Why is this changing?"
            value={reasonCode}
            onChange={(e) => setReasonCode(e.target.value)}
            disabled={saving}
            fullWidth
            size="small"
            required
          >
            {reasonCodes.map((option) => (
              <MenuItem key={option.code} value={option.code}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="What supports this change?"
            value={reasonDetail}
            onChange={(e) => setReasonDetail(e.target.value)}
            disabled={saving}
            fullWidth
            size="small"
            required
            multiline
            minRows={2}
            placeholder="Called HR on 25 Aug; they confirmed the legal name is Northridge Logistics Inc."
            helperText={`This is permanent and attributed to you. Minimum ${MIN_REASON_DETAIL_LENGTH} characters.`}
          />

          <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
            <Typography variant="body2">
              This records the correction. It does <strong>not</strong> re-run verification or
              re-evaluate the application.
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
        {validity?.blockedReason ? (
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
            {validity.blockedReason}
          </Typography>
        ) : (
          <Chip size="small" variant="outlined" label="Ready to save" sx={{ mr: 'auto' }} />
        )}
        <Button onClick={onClose} disabled={saving} size="small" color="inherit">
          Cancel
        </Button>
        <Button
          onClick={() => onSubmit({ value: value.trim(), reasonCode, reasonDetail: reasonDetail.trim() })}
          disabled={saving || !validity?.canSubmit}
          variant="contained"
          size="small"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {saving ? 'Saving' : 'Save change'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
